import { autoinject, computedFrom, observable, transient, useView } from "aurelia-framework";
import { Router } from "aurelia-router";
import * as moment from "moment";
import { Operation } from "../../../../../../client-validation-system/base-validations/compare-validation";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel, VetClientModel, VetModel } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CoachmarkClientService } from "../../../../../../services/coachmark/coachmark-client.service";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { Guid } from "../../../../../../utilities/guid";
import { EntityFinderSearch } from "../../../../../components/entity-finder/entity-finder-search";
import { BaseVetOrder } from "../base-vet-order";
import { CustomerService } from "../../../../../../services/modules/customer-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { Reflection } from "../../../../../../utilities/reflection";
import { OrderModel } from "../../../../../../models/rightstore/rightstore.models";

@autoinject()
@transient()
@useView('views/pages/modules/orders/plugins/tanker-vetting-with-sire-banner/tanker-vetting-with-sire-banner.html')
export class TankerVettingWithSireBanner extends BaseVetOrder {

    viewTitle: string = "Tanker Vetting With Sire Banner";
    public portEntityTypes: EntityTypeConfigurationModel[];
    public vesselEntityTypes: EntityTypeConfigurationModel[];
    public vet: VetClientModel;
    public vesselVM: EntityFinderSearch;
    public linkFullVetCheck: boolean;
    public voyageFromVM: EntityFinderSearch;
    public voyageToVM: EntityFinderSearch;
    public customerId: number;
    public confirmSanctionedCountry: boolean = false;
    public confirmNonSanctionedCountry: boolean = false;
    public unknownPortFrom: boolean = false;
    public unknownPortTo: boolean = false;
    public sanctionedPortFrom: boolean = false;
    public geoPoliticalPortFrom: boolean = false;
    public sanctionedPortTo: boolean = false;
    public geoPoliticalPortTo: boolean = false;
    public canEditCorrespondenceEmails: boolean = false;
    public showIncoTerms: boolean = false;
    public mandatoryIncoTerms: boolean = false;
    public transactionNumber: string;
    public timeCharterStartDate?: Date;
    public timeCharterEndDate?: Date;

    async activate(data: { vesselId: number, order: OrderModel, vessel: VesselDetailModel, vet: VetClientModel, ruleCollectionId: number, customerId: number, businessUnitId: number, isTest: boolean }) {
        this.vesselId = data.vesselId;
        this.canEditVessel = !data.vesselId || data.order?.orderGuid != null;
        this.vet = data.vet;
        this.cargoTypeCategoryId = data.vessel ? data.vessel.cargoTypeCategoryId : null;
        this.ruleCollectionId = data.ruleCollectionId;

        if (!data || data.businessUnitId == null || data.businessUnitId <= 0) {
            this.customerId = this.userClientService.currentUser.settings.customerId;
            if (this.customerId == null || this.customerId <= 0) {
                this.customerId = await this.commonCustomerService.getCustomerId();
            }

            this.businessUnitId = this.userClientService.currentUser.settings.businessUnitId;
            if (this.businessUnitId == null || this.businessUnitId <= 0) {
                this.businessUnitId = await this.commonCustomerService.getBusinessUnitId();
            }
        }
        else {
            this.customerId = data.customerId;
            this.businessUnitId = data.businessUnitId;
            this.isTest = data.isTest;
        }

        await this.loadbusinessUnit();
    }

    emails: SubtaskCorrespondenceEmailModel[] = [{
        email: "",
        isShipManagerEmail: false,
        isFromConfig: false,
        emailRecipientTypeId: EmailRecipientTypeEnum.To
    }];
    portFromId: number;
    portFromModel: { id: number, name: string, countryName: string, sanctionedText: string, isSactioned: boolean };
    isTest: boolean;
    businessUnitId: number;
    portToId: number;
    departureDate: Date = null;
    arrivalDate: Date = null;
    cargoTypeId: number;
    cargoTypeValue: LookupValue;
    vetId: number;
    parentVetId: number;
    cargoTypeCategoryId?: number;
    berth: string;
    comments: string;
    dischargePortCountryId: number;
    loadPortCountryId: number;
    dischargePort: string;
    loadPort: string;
    validator: ClientSideValidator;
    validatorEmails: ClientSideValidator;
    showBerthInVetRequest: boolean;
    impersonateBUForRequestingVet: boolean;
    onConfirm: () => void;
    onViewVet: (id: number) => void;
    ruleCollectionId: number;
    _manualDischargePort: boolean = false;
    _manualLoadPort: boolean = false;
    documentOfComplianceEmail: string;
    vetRequiresMoreInformationEmails: SubtaskCorrespondenceEmailModel[] = [];
    public canSubmit: boolean = true;
    sireProvided: boolean = false;

    @computedFrom("sireProvided", "sanctionedPorts", "confirmNonSanctionedCountry", "sanctionedPorts", "confirmSanctionedCountry")
    public get disableSubmitInternal(): boolean {
        return !this.sireProvided
            || (!this.sanctionedPorts && !this.confirmNonSanctionedCountry)
            || (this.sanctionedPorts && !this.confirmSanctionedCountry) ||
            this.geoPoliticalPortFrom || this.geoPoliticalPortTo;
    }

    @computedFrom(Reflection.getPropertyPath<TankerVettingWithSireBanner>(z => z._manualLoadPort))
    get manualLoadPort() {
        return this._manualLoadPort
    }

    @computedFrom(Reflection.getPropertyPath<TankerVettingWithSireBanner>(z => z._manualDischargePort))
    get manualDischargePort() {
        return this._manualDischargePort
    }

    set manualLoadPort(x: boolean) {
        this._manualLoadPort = x;
        if (this._manualLoadPort) {
            this.voyageFromVM.removeFilter();
        }
    }

    set manualDischargePort(x: boolean) {
        this._manualDischargePort = x;
        if (this._manualDischargePort) {
            this.voyageToVM.removeFilter();
        }
    }

    @observable()
    liaseWithShipManagers: boolean = false;

    contactShipManagersDirectly: boolean = false;

    vetForSTS: boolean;
    charterTypeId: number;

    showSTS: boolean;
    showTC: boolean;
    showTransactionNumber: boolean;

    @computedFrom("cargoTypeValue")
    get isOtherCargo() {
        return this.cargoTypeValue ? this.cargoTypeValue.additionalData['code'] == 'OTH' : false;
    }

    @computedFrom("sanctionedPortTo", "sanctionedPortFrom")
    public get sanctionedPorts(): boolean {
        return this.sanctionedPortTo || this.sanctionedPortFrom;
    }

    constructor(protected vesselService: ManageVesselService, private dialogClientService: DialogClientService,
        private userClaimsService: UserClaimsService, private entityValidationsService: EntityValidationsService, private router: Router,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private userClientService: UserClientService,
        private coachmarkClientService: CoachmarkClientService,
        private commonCustomerService: CommonCustomerService,
        private customerService: CustomerService) {
        super(vesselService);

        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.vesselEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["VesselSearch"]).filter(x => x.key == "BasicVesselSearch");
        this.impersonateBUForRequestingVet = userClaimsService.hasPermission(Claims.ImpersonateBUForRequestingVet);
        this.showBerthInVetRequest = this.userClaimsService.hasPermission(Claims.ShowBerthInVetRequest);
        this.showSTS = this.userClaimsService.hasPermission(Claims.ShowShipToShip);
        this.showTC = this.userClaimsService.hasPermission(Claims.ShowCharterType);
        this.showTransactionNumber = this.userClaimsService.hasPermission(Claims.ShowTransactionNumber);
        this.canEditCorrespondenceEmails = this.userClaimsService.hasPermission(Claims.CanEditCorrespondenceEmails);
        this.showIncoTerms = this.userClaimsService.hasPermission(Claims.ShowIncoTerms);
        this.mandatoryIncoTerms = this.showIncoTerms && this.userClaimsService.hasPermission(Claims.MandatoryIncoTerms);

        this.charterTypeId = this.showTC ? null : this.charterTypeId;
        this.vetForSTS = this.showSTS ? false : this.vetForSTS;
    }

    attached() {
        if (this.vet) {
            this.vetId = this.vet.vetId;
            this.emails = !this.linkFullVetCheck ? this.vet.initialEmailsForCorrespondence.filter(x => !x.isShipManagerEmail) :
                [{
                    email: this.vet && this.vet.requestedByEmail ? this.vet.requestedByEmail : this.userClientService.currentUser.email,
                    emailRecipientTypeId: EmailRecipientTypeEnum.To
                }];
            this.portFromId = this.vet.portOfLoadId;
            this.departureDate = this.vet.dateOfDeparture;
            this.portToId = this.vet.portOfDischargeId;
            this.arrivalDate = this.vet.dateOfArrival;
            this.cargoTypeId = this.vet.cargoId;
            this.berth = this.vet.berth;
            this.comments = this.vet.vetComments;
            this.liaseWithShipManagers = this.vet.liaseWithShipManagers;
            this.vetForSTS = this.vet.vetForSTS;
            this.charterTypeId = this.vet.charterTypeId;
            this.voyageFromVM.set({ entityTypeUri: this.portEntityTypes[0].uri, id: this.portFromId });
            this.voyageToVM.set({ entityTypeUri: this.portEntityTypes[0].uri, id: this.portToId });
            this.parentVetId = this.vet.parentVetId;
        } else {
            this.emails = [{
                email: this.vet && this.vet.requestedByEmail ? this.vet.requestedByEmail : this.userClientService.currentUser.email,
                emailRecipientTypeId: EmailRecipientTypeEnum.To
            }];

            if (this.vetRequiresMoreInformationEmails && this.vetRequiresMoreInformationEmails.length > 0) {
                const vrmiEmails = this.vetRequiresMoreInformationEmails.map(x => {
                    if (x && x.email == "<requester>")
                        x.email = this.vet && this.vet.requestedByEmail ? this.vet.requestedByEmail : this.userClientService.currentUser.email;

                    if (!x.emailRecipientTypeId)
                        x.emailRecipientTypeId = EmailRecipientTypeEnum.To;

                    return x;
                });

                this.emails = this.emails.concat(vrmiEmails);
            }
        }

        const requesterList = this.getEmailList();
        if (requesterList.length == 0)
            this.addEmail();
        requesterList[0].canEdit = true;

        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Vet Request Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Your email is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };

        this.validatorEmails = this.entityValidationsService.getValidator(this.emails, rulesHolderEmails);
        this.coachmarkClientService.start(this.coachmarkClientService.getFileFromPath(RightShipDialogServiceConstants.URLs.RequestVetDialog));

        if (this.vesselId) {
            this.vesselVM.set({ entityTypeUri: this.vesselEntityTypes[0].uri, id: this.vesselId }).then(() => {
                this.setVessel(this.vesselVM.selectedEntity);
            });
        }
    }

    detached() {
        if (this.validator && this.validator.dispose)
            this.validator.dispose();
        if (this.validatorEmails && this.validatorEmails.dispose)
            this.validatorEmails.dispose();
    }

    close() {
        if (this.onConfirm)
            this.onConfirm();

        this.dialogClientService.hide();
    }

    async loadbusinessUnit() {
        const canContactShipManagersDirectly = await this.customerService.canContactShipManagersDirectly(this.businessUnitId);
        this.contactShipManagersDirectly = this.liaseWithShipManagers = canContactShipManagersDirectly;
    }

    navigateToVet() {
        if (this.onViewVet) {
            this.onViewVet(this.vetId);
            this.dialogClientService.hide();
        } else {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.vetId, source: "vessels", forceLoad: true })
        }
    }

    removeEmail(i) {
        const list = this.getEmailList();
        list.splice(i, 1);
    }

    addEmail() {
        const list = this.getEmailList();

        list.push({
            email: "",
            isShipManagerEmail: false,
            isFromConfig: false,
            emailRecipientTypeId: EmailRecipientTypeEnum.To
        });
    }

    private getEmailList(): SubtaskCorrespondenceEmailModel[] {
        let list = null;
        list = this.emails;
        return list;
    }

    today() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    estDepartureMinDate() {
        if (this.departureDate) {
            return this.departureDate && moment(this.departureDate).isAfter(moment(), 'd') ?
                this.departureDate : this.today();
        }
        else return null;
    }

    estArrivalMinDate() {
        if (this.arrivalDate) {
            return this.arrivalDate && moment(this.arrivalDate).isAfter(moment(), 'd') ?
                this.arrivalDate : this.today();
        }
        else return null;
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Vessel Name / IMO is required",
            parameters: null, property: "vesselId",
            validationType: ValidationTypeEnum.Required
        },
        {
            errorMessage: "Cargo Type is required",
            parameters: null, property: "cargoTypeId",
            validationType: ValidationTypeEnum.Required
        }];

        if (this.manualLoadPort) {
            rules.push({
                errorMessage: "Voyage From Country is required",
                parameters: null, property: "loadPortCountryId",
                validationType: ValidationTypeEnum.Required
            });
            rules.push({
                errorMessage: "Voyage From Port Name is required",
                parameters: null, property: "loadPort",
                validationType: ValidationTypeEnum.Required
            });
        } else {
            rules.push({
                errorMessage: "Voyage From is required",
                parameters: null, property: "portFromId",
                validationType: ValidationTypeEnum.Required
            });
        }

        if (this.manualDischargePort) {
            rules.push({
                errorMessage: "Voyage To Country is required",
                parameters: null, property: "dischargePortCountryId",
                validationType: ValidationTypeEnum.Required
            });
            rules.push({
                errorMessage: "Voyage To Port Name is required",
                parameters: null, property: "dischargePort",
                validationType: ValidationTypeEnum.Required
            });
        } else {
            rules.push({
                errorMessage: "Voyage To is required",
                parameters: null, property: "portToId",
                validationType: ValidationTypeEnum.Required
            });
        }

        rules.push({
            errorMessage: "Date of load cannot be set in the past",
            parameters: {
                property: "departureDate",
                operation: Operation.GreaterThanOrEqual,
                value: this.estDepartureMinDate()
            }, property: "departureDate",
            validationType: ValidationTypeEnum.Compare
        });

        rules.push({
            errorMessage: "Date of discharge cannot be set in the past",
            parameters: {
                property: "arrivalDate",
                operation: Operation.GreaterThanOrEqual,
                value: this.estArrivalMinDate()
            }, property: "arrivalDate",
            validationType: ValidationTypeEnum.Compare
        });

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Vet Request",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    unverifiedRestrictionsPortCodes = ["DS00007", "GENERIC-002", "GENERIC-004"];

    public onVoyageFromSelected(port) {
        this.portFromId = port ? port.id : null;

        this.unknownPortFrom = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortFrom = !this.unknownPortFrom && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
        this.geoPoliticalPortFrom = !this.unknownPortFrom && port && (port.isGeopoliticalRisk && port.isGeopoliticalRisk.toString().toLowerCase() == 'true');
    }

    public manualLoadPortChanged(model: any) {
        this.sanctionedPortFrom = model.additionalData.isSanctioned;
        this.geoPoliticalPortFrom = model.additionalData.isGeopoliticalRisk;
    }

    public manualLoadPortCheckChanged() {
        this.sanctionedPortFrom = false;
        this.geoPoliticalPortFrom = false;
        this.unknownPortFrom = false;
    }

    public onVoyageToSelected(port) {
        this.portToId = port ? port.id : null;

        this.unknownPortTo = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortTo = !this.unknownPortTo && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
        this.geoPoliticalPortTo = !this.unknownPortTo && port && (port.isGeopoliticalRisk && port.isGeopoliticalRisk.toString().toLowerCase() == 'true');
    }

    public manualDischargePortChanged(model: any) {
        this.sanctionedPortTo = model.additionalData.isSanctioned;
        this.geoPoliticalPortTo = model.additionalData.isGeopoliticalRisk;
    }

    public manualDischargePortCheckChanged() {
        this.sanctionedPortTo = false;
        this.geoPoliticalPortTo = false;
        this.unknownPortTo = false;
    }

    public async isValid(): Promise<boolean> {
        this.setValidator();
        return await this.validator.validate().then(x => {
            return x.valid
        });
    }

    public getMetaModel() {
        const vet: VetModel = {
            vesselId: this.vesselId,
            portFromId: this.portFromId,
            portToId: this.portToId,
            departureDate: this.departureDate,
            arrivalDate: this.arrivalDate,
            cargoTypeId: this.cargoTypeId,
            comments: this.comments,
            berth: this.berth,
            vetId: this.vetId ? this.vetId : 0,
            liaseWithShipManagers: this.liaseWithShipManagers,
            vetForSTS: this.vetForSTS,
            charterTypeId: this.charterTypeId,
            businessUnitId: this.businessUnitId,
            isTest: this.isTest,
            transactionNumber: this.transactionNumber,
            parentVetId: this.parentVetId,
            identifier: Guid.generateGuid(),
            ruleCollectionId: this.ruleCollectionId,
            timeCharterStartDate: this.timeCharterStartDate,
            timeCharterEndDate: this.timeCharterEndDate
        };
        const vetCorrespondenceEmails = this.emails.filter(x => x.email);

        const metaModel = {
            vetCorrespondenceEmails: vetCorrespondenceEmails.map(a => {
                return {
                    email: a.email,
                    isShipManagerEmail: a.isShipManagerEmail,
                    isFromConfig: a.isFromConfig,
                    emailRecipientTypeId: a.emailRecipientTypeId
                };
            }),
            manualDischargePort: this.manualDischargePort,
            manualLoadPort: this.manualLoadPort,
            dischargePortCountryId: this.dischargePortCountryId,
            loadPortCountryId: this.loadPortCountryId,
            dischargePort: this.dischargePort,
            loadPort: this.loadPort,
            vet: vet
        };

        return metaModel;
    }

    protected onVesselSelectedInternal = (vessel: any) => {
        if (!vessel) return;
        this.cargoTypeCategoryId = vessel ? vessel.vesselCargoTypeCategory : null;
    }
}